import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, Image as ImageIcon, MessageSquare, Phone, Mail, Eye, EyeOff, FileText, Layout, Home as HomeIcon, User as UserIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ImageUploader from '../components/ImageUploader';
import HeroRotationManager from '../components/HeroRotationManager';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact single-image field with upload + preview
const SingleImageField = ({ label, hint, value, onChange, testId }) => (
  <div>
    <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
      {label}
    </label>
    {hint && <p className="text-xs text-muted-foreground mb-3 italic">{hint}</p>}
    <div className="flex items-start gap-6">
      <div className="w-32 h-32 flex-shrink-0 border border-border bg-secondary overflow-hidden">
        {value ? (
          <img src={value} alt={label} className="w-full h-full object-cover" data-testid={testId ? `${testId}-preview` : undefined} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-muted-foreground/40" strokeWidth={1} />
          </div>
        )}
      </div>
      <div className="flex-1">
        <ImageUploader
          existingImages={value ? [value] : []}
          onUploadComplete={(urls) => onChange(urls[urls.length - 1] || '')}
          hideLabel
          hidePreviewGrid
          buttonText={value ? 'Enviar nova imagem para substituir' : 'Clique para enviar uma imagem'}
        />
      </div>
    </div>
  </div>
);

const Settings = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [products3D, setProducts3D] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !user.is_admin) {
      toast.error('Acesso negado. Apenas administradores.');
      navigate('/');
      return;
    }
    fetchSettings();
    fetchProducts3D();
  }, [authLoading, user, navigate]);

  const fetchProducts3D = async () => {
    try {
      const res = await axios.get(`${API_URL}/products`);
      setProducts3D((res.data || []).filter((p) => p.model_3d_url));
    } catch (_) {
      // fallback silencioso — select mostra apenas '— Nenhum —'
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/settings`, settings);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (authLoading || loading) {
    return (
      <div className="py-20 text-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (!user || !user.is_admin) return null;

  return (
    <div data-testid="settings-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between mb-16">
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
            Configurações do Site
          </h1>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 bg-secondary text-primary px-6 py-3 uppercase tracking-widest text-xs hover:bg-primary hover:text-white border border-primary transition-all duration-500"
            >
              {showPreview ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              {showPreview ? 'Ocultar' : 'Ver'} Preview
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings-button"
              className="flex items-center gap-2 bg-primary text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-black transition-all duration-500 disabled:opacity-50"
            >
              <Save className="w-4 h-4" strokeWidth={1.5} />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>

        {/* Preview Section */}
        {showPreview && (
          <div className="mb-16 border-2 border-primary p-8">
            <h2 className="font-accent text-xs uppercase tracking-widest mb-8 text-center">
              Preview das Alterações
            </h2>
            
            {/* Hero Preview */}
            <div className="relative h-96 flex items-center mb-8 overflow-hidden">
              <div className="absolute inset-0">
                <img
                  src={settings?.hero_image || 'https://via.placeholder.com/1920x600?text=Imagem+Hero'}
                  alt="Hero Preview"
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.src = 'https://via.placeholder.com/1920x600?text=Imagem+Hero'}
                />
                <div className="absolute inset-0 bg-primary opacity-30"></div>
              </div>
              <div className="relative z-10 px-12 max-w-4xl">
                <h1 className="font-heading text-5xl text-white font-light tracking-tight leading-tight mb-4 whitespace-pre-line">
                  {settings?.hero_title || 'Título Principal'}
                </h1>
                <p className="text-lg font-light leading-relaxed text-white/90 max-w-xl">
                  {settings?.hero_subtitle || 'Subtítulo da página inicial'}
                </p>
              </div>
            </div>

            {/* Contact Info Preview */}
            <div className="bg-secondary p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2">Email</p>
                <p className="text-sm">{settings?.contact_email || 'email@exemplo.com'}</p>
              </div>
              <div>
                <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2">Telefone</p>
                <p className="text-sm">{settings?.contact_phone || '(11) 0000-0000'}</p>
              </div>
              <div>
                <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2">WhatsApp</p>
                <p className="text-sm">{settings?.contact_whatsapp || '5511000000000'}</p>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="text-xs text-muted-foreground italic">
                * Este é apenas um preview. Clique em "SALVAR" para aplicar as mudanças ao site.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-12">
          {/* LOGOS */}
          <section className="border-b border-border pb-12">
            <div className="flex items-center gap-3 mb-8">
              <ImageIcon className="w-5 h-5" strokeWidth={1.5} />
              <h2 className="font-accent text-xs uppercase tracking-widest">Logos da Marca</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <SingleImageField
                label="Logo escura (Navbar / fundo claro)"
                hint="PNG transparente · horizontal 2:1 a 3:1 · ideal 800 × 400 px"
                value={settings?.logo_dark_image}
                onChange={(url) => updateField('logo_dark_image', url)}
                testId="logo-dark"
              />
              <SingleImageField
                label="Logo clara (Rodapé / fundo escuro)"
                hint="PNG transparente branco · horizontal 2:1 a 3:1 · ideal 800 × 400 px"
                value={settings?.logo_light_image}
                onChange={(url) => updateField('logo_light_image', url)}
                testId="logo-light"
              />
            </div>
          </section>

          {/* PÁGINA INICIAL (HOME) */}
          <section className="border-b border-border pb-12">
            <div className="flex items-center gap-3 mb-8">
              <HomeIcon className="w-5 h-5" strokeWidth={1.5} />
              <h2 className="font-accent text-xs uppercase tracking-widest">Página Inicial (Home)</h2>
            </div>
            
            <div className="space-y-8">
              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Título Principal (Hero)
                </label>
                <textarea
                  value={settings?.hero_title || ''}
                  onChange={(e) => updateField('hero_title', e.target.value)}
                  rows={2}
                  className="w-full bg-transparent border border-border focus:border-primary p-4 focus:ring-0 outline-none transition-colors duration-300 resize-none"
                />
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Subtítulo (Hero)
                </label>
                <textarea
                  value={settings?.hero_subtitle || ''}
                  onChange={(e) => updateField('hero_subtitle', e.target.value)}
                  rows={2}
                  className="w-full bg-transparent border border-border focus:border-primary p-4 focus:ring-0 outline-none transition-colors duration-300 resize-none"
                />
              </div>

              <SingleImageField
                label="Imagem de fundo do topo (Hero)"
                hint="JPG horizontal 16:9 · ideal 1920 × 1080 px · deixe respiro nas bordas para o texto"
                value={settings?.hero_image}
                onChange={(url) => updateField('hero_image', url)}
                testId="hero-image"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                <SingleImageField
                  label='Card "Residencial" (Coleções)'
                  hint="JPG QUADRADA 1:1 · ideal 1200 × 1200 px · terá texto sobreposto"
                  value={settings?.home_residential_image}
                  onChange={(url) => updateField('home_residential_image', url)}
                  testId="home-residential"
                />
                <SingleImageField
                  label='Card "Corporativo" (Coleções)'
                  hint="JPG QUADRADA 1:1 · ideal 1200 × 1200 px · terá texto sobreposto"
                  value={settings?.home_corporate_image}
                  onChange={(url) => updateField('home_corporate_image', url)}
                  testId="home-corporate"
                />
              </div>

              <SingleImageField
                label='Imagem "Qualidade Artesanal" (seção meio da Home)'
                hint="JPG vertical 3:4 · ideal 900 × 1200 px · aparece ao lado direito do texto"
                value={settings?.about_home_image}
                onChange={(url) => updateField('about_home_image', url)}
                testId="home-artisanal"
              />

              {/* Vitrine 3D — peça em destaque na Home */}
              <div className="pt-6 mt-6 border-t border-border">
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                  Vitrine 3D da Home (peça em destaque)
                </label>
                <p className="text-xs text-muted-foreground mb-4 italic">
                  Escolha um produto (fallback) OU configure um rodízio semanal automático abaixo.
                  Se a fila do rodízio estiver preenchida, ela terá prioridade sobre este campo.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">
                      Produto (só produtos com modelo 3D aparecem aqui)
                    </label>
                    <select
                      data-testid="hero-3d-product-select"
                      value={settings?.hero_3d_product_id || ''}
                      onChange={(e) => updateField('hero_3d_product_id', e.target.value || null)}
                      className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 text-sm outline-none"
                    >
                      <option value="">— Nenhum (ocultar vitrine) —</option>
                      {/* Preserva a option do id salvo mesmo enquanto a lista carrega */}
                      {settings?.hero_3d_product_id &&
                        !(products3D || []).some((p) => p.id === settings.hero_3d_product_id) && (
                          <option value={settings.hero_3d_product_id}>
                            (produto atual — recarregando…)
                          </option>
                        )}
                      {(products3D || []).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">
                      Título da seção
                    </label>
                    <input
                      type="text"
                      data-testid="hero-3d-title"
                      value={settings?.hero_3d_title || ''}
                      onChange={(e) => updateField('hero_3d_title', e.target.value)}
                      placeholder="Peça em Destaque"
                      className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 text-sm outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">
                      Subtítulo
                    </label>
                    <input
                      type="text"
                      data-testid="hero-3d-subtitle"
                      value={settings?.hero_3d_subtitle || ''}
                      onChange={(e) => updateField('hero_3d_subtitle', e.target.value)}
                      placeholder="Explore em 360° a obra da semana"
                      className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 text-sm outline-none"
                    />
                  </div>
                </div>

                <HeroRotationManager
                  allProducts3D={products3D}
                  rotation={settings?.hero_3d_rotation || []}
                  currentEffectiveId={settings?.hero_3d_current_product_id}
                  onChange={(newRotation) => updateField('hero_3d_rotation', newRotation)}
                />
              </div>
            </div>
          </section>

          {/* Contact Info */}
          <section className="border-b border-border pb-12">
            <div className="flex items-center gap-3 mb-8">
              <Phone className="w-5 h-5" strokeWidth={1.5} />
              <h2 className="font-accent text-xs uppercase tracking-widest">Informações de Contato</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  E-mail
                </label>
                <input
                  type="email"
                  value={settings?.contact_email || ''}
                  onChange={(e) => updateField('contact_email', e.target.value)}
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                />
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Telefone
                </label>
                <input
                  type="tel"
                  value={settings?.contact_phone || ''}
                  onChange={(e) => updateField('contact_phone', e.target.value)}
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                />
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  WhatsApp (com código do país)
                </label>
                <input
                  type="text"
                  value={settings?.contact_whatsapp || ''}
                  onChange={(e) => updateField('contact_whatsapp', e.target.value)}
                  placeholder="5511999999999"
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                />
              </div>

              <div className="md:col-span-2">
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Endereço
                </label>
                <textarea
                  value={settings?.contact_address || ''}
                  onChange={(e) => updateField('contact_address', e.target.value)}
                  rows={3}
                  className="w-full bg-transparent border border-border focus:border-primary p-4 focus:ring-0 outline-none transition-colors duration-300 resize-none"
                />
              </div>
            </div>
          </section>

          {/* Social Media */}
          <section className="border-b border-border pb-12">
            <div className="flex items-center gap-3 mb-8">
              <Mail className="w-5 h-5" strokeWidth={1.5} />
              <h2 className="font-accent text-xs uppercase tracking-widest">Redes Sociais</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Instagram URL
                </label>
                <input
                  type="url"
                  value={settings?.social_instagram || ''}
                  onChange={(e) => updateField('social_instagram', e.target.value)}
                  placeholder="https://instagram.com/..."
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                />
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Facebook URL
                </label>
                <input
                  type="url"
                  value={settings?.social_facebook || ''}
                  onChange={(e) => updateField('social_facebook', e.target.value)}
                  placeholder="https://facebook.com/..."
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                />
              </div>
            </div>
          </section>

          {/* PÁGINA SOBRE NÓS */}
          <section className="border-b border-border pb-12">
            <div className="flex items-center gap-3 mb-8">
              <UserIcon className="w-5 h-5" strokeWidth={1.5} />
              <h2 className="font-accent text-xs uppercase tracking-widest">Página &quot;Sobre Nós&quot;</h2>
            </div>
            
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <SingleImageField
                  label="Foto 1 — Fhiama na poltrona"
                  hint="JPG vertical 4:5 · ideal 800 × 1000 px · aparece no bloco 1 (topo, à direita do texto)"
                  value={settings?.about_page_chair_image}
                  onChange={(url) => updateField('about_page_chair_image', url)}
                  testId="about-chair"
                />
                <SingleImageField
                  label="Foto 2 — Fhiama com luminária"
                  hint="JPG vertical 4:5 · ideal 800 × 1000 px · aparece no bloco 2 (à esquerda do texto)"
                  value={settings?.about_page_lamp_image}
                  onChange={(url) => updateField('about_page_lamp_image', url)}
                  testId="about-lamp"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Texto Seção 1 (Excelência em Cada Detalhe)
                  </label>
                  <textarea
                    value={settings?.about_page_text_1 || ''}
                    onChange={(e) => updateField('about_page_text_1', e.target.value)}
                    rows={4}
                    className="w-full bg-transparent border border-border focus:border-primary p-4 focus:ring-0 outline-none transition-colors duration-300 resize-none"
                  />
                </div>

                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Texto Seção 2 (Materiais Nobres)
                  </label>
                  <textarea
                    value={settings?.about_page_text_2 || ''}
                    onChange={(e) => updateField('about_page_text_2', e.target.value)}
                    rows={4}
                    className="w-full bg-transparent border border-border focus:border-primary p-4 focus:ring-0 outline-none transition-colors duration-300 resize-none"
                  />
                </div>

                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Texto Seção 3 (Compromisso com Qualidade)
                  </label>
                  <textarea
                    value={settings?.about_page_text_3 || ''}
                    onChange={(e) => updateField('about_page_text_3', e.target.value)}
                    rows={4}
                    className="w-full bg-transparent border border-border focus:border-primary p-4 focus:ring-0 outline-none transition-colors duration-300 resize-none"
                  />
                </div>

                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Texto Seção 4 (Reputação)
                  </label>
                  <textarea
                    value={settings?.about_page_text_4 || ''}
                    onChange={(e) => updateField('about_page_text_4', e.target.value)}
                    rows={4}
                    className="w-full bg-transparent border border-border focus:border-primary p-4 focus:ring-0 outline-none transition-colors duration-300 resize-none"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Footer Section */}
          <section className="border-b border-border pb-12">
            <div className="flex items-center gap-3 mb-8">
              <Layout className="w-5 h-5" strokeWidth={1.5} />
              <h2 className="font-accent text-xs uppercase tracking-widest">Rodapé (Footer)</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Tagline do Rodapé
                </label>
                <textarea
                  value={settings?.about_tagline || ''}
                  onChange={(e) => updateField('about_tagline', e.target.value)}
                  rows={2}
                  placeholder="Criando espaços extraordinários com móveis de alto padrão desde 2010."
                  className="w-full bg-transparent border border-border focus:border-primary p-4 focus:ring-0 outline-none transition-colors duration-300 resize-none"
                />
              </div>
            </div>
          </section>

          {/* Business Hours Section */}
          <section>
            <div className="flex items-center gap-3 mb-8">
              <Phone className="w-5 h-5" strokeWidth={1.5} />
              <h2 className="font-accent text-xs uppercase tracking-widest">Horário de Atendimento</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Dias de Semana
                </label>
                <input
                  type="text"
                  value={settings?.hours_weekday || ''}
                  onChange={(e) => updateField('hours_weekday', e.target.value)}
                  placeholder="Segunda a Sexta: 9h às 18h"
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                />
              </div>

              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Final de Semana
                </label>
                <input
                  type="text"
                  value={settings?.hours_weekend || ''}
                  onChange={(e) => updateField('hours_weekend', e.target.value)}
                  placeholder="Sábado: 10h às 14h"
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3 focus:ring-0 outline-none transition-colors duration-300"
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Settings;